'use client'

import Script from 'next/script'
import { Suspense, useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { TRACKED_HOST_REGEX_SOURCE } from '@/lib/ga-tracked-host'
import { buildGtagConsentLines } from '@/lib/consent/signal'
import { useIsHydrated } from '@/hooks/use-is-hydrated'

/**
 * Google Analytics 4 Component
 *
 * Loads GA4 tracking for the site. Measurement ID comes from env var.
 * This is a client component that injects the gtag.js script.
 *
 * Required env var: NEXT_PUBLIC_GA_MEASUREMENT_ID
 * Format: G-XXXXXXXXXX
 *
 * Consent Mode v2 (FRIDGE-115): the inline snippet sets a consent DEFAULT of
 * denied for every storage type, scoped with `region:` to the EEA + UK + CH
 * list in lib/consent/signal.ts, BEFORE `gtag('js', …)`. Google applies that
 * default only to visitors it places in those regions, so the US path is
 * unchanged. A stored Allow (`fk_consent=granted`, written by the banner) is
 * replayed as a consent UPDATE right after the default, and a fresh Allow is
 * sent live by lib/consent/client.ts. The list and the cookie test are
 * interpolated from the shared module so the browser snippet and the unit
 * tests cannot drift.
 *
 * Guards (2026-07-12 tagging audit):
 * - NODE_ENV gate: `next dev` never sends — localhost was 60% of all recorded
 *   pageviews in the production property. Verified working post-deploy:
 *   since 07/13/2026 every page_view carries hostName=fridge.com.
 * - Hostname gate inside the snippet: Vercel previews build with
 *   NODE_ENV=production, so the config call itself is scoped to the apex and
 *   subdomains of fridge.com. The pattern lives in lib/ga-tracked-host.ts
 *   (interpolated below) so the gate is unit-tested and cannot drift.
 * - SPA pageviews: gtag's `config` fires page_view once per full load only;
 *   RouteChangePageViews reports App Router client-side navigations.
 *
 * Loading strategy (FRIDGE-72, 07/25/2026): `afterInteractive`, not
 * `lazyOnload`. lazyOnload defers the tag until after the window `load`
 * event plus browser idle — on heavy pages (1.2 MB HTML) high-intent
 * visitors who click an affiliate link and leave were never counted, and
 * client-side navigations in that window were dropped. afterInteractive
 * runs right after hydration: the gtag stub and config exist before any
 * user navigation is possible, which also makes the window.gtag guard in
 * RouteChangePageViews effectively unreachable rather than a real drop.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

function RouteChangePageViews() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isInitialLoad = useRef(true)

  useEffect(() => {
    // The initial page_view rides the gtag config call; only client-side
    // route changes are reported here.
    if (isInitialLoad.current) {
      isInitialLoad.current = false
      return
    }
    // With afterInteractive the inline snippet (which defines window.gtag)
    // runs before hydration completes, so this guard is a safety net for
    // non-production hosts where the snippet's hostname gate skipped config.
    if (typeof window.gtag !== 'function') return
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
    })
  }, [pathname, searchParams])

  return null
}

export default function GoogleAnalytics() {
  const hydrated = useIsHydrated()
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID

  // Don't load if no measurement ID, and never from `next dev`
  if (!measurementId || process.env.NODE_ENV !== 'production') {
    return null
  }

  return (
    <>
      {/* Global Site Tag (gtag.js) - Google Analytics */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
        strategy="afterInteractive"
        onError={() => {
          // Silently handle - GA blocked by ad blocker or network issue
        }}
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          if (/${TRACKED_HOST_REGEX_SOURCE}/.test(window.location.hostname)) {
            ${buildGtagConsentLines()}
            gtag('js', new Date());
            gtag('config', '${measurementId}', {
              page_title: document.title,
              page_location: window.location.href,
              send_page_view: true
            });
          }
        `}
      </Script>
      <Suspense fallback={null}>
        {/* Query tracking is client-only; mounting after hydration keeps its
            useSearchParams hook out of static HTML without delaying the tag. */}
        {hydrated && <RouteChangePageViews />}
      </Suspense>
    </>
  )
}
